import { Popover } from '@mui/material';
import type { Filter } from 'contexts/preferences/listing-params/types';
import { SavedSearchPanelContent } from './saved-search-panel-content';
import type { MapBounds } from 'domain/saved-search';

/** What the top bar needs when a saved search is selected: display info for the bookmark + the filter to apply. */
export interface SelectedSavedSearch {
  id: string;
  title: string;
  imageUrl?: string | null;
  /** The saved search's full filter (incl. latitude/longitude/zoom) — applied to the page + map on select. */
  filter: Filter;
}

export type SavedSearchPanelProps = {
  open: boolean;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  /** The search params currently applied on the page — persisted when the user saves. */
  currentFilter?: Filter;
  /**
   * The map's current rectangle, recorded on the saved search when the filter
   * has no area slug to locate it by.
   */
  mapBounds?: MapBounds | null;
  /** True on a page where the map is the search; the saved search drops any slug. */
  locatedByMap?: boolean;
  /** Called when the user picks "All Saved Searches". */
  onViewAllSavedSearches?: () => void;
  /** Id of the currently-selected saved search (drives the card highlight). */
  selectedId?: string;
  /** Called when a card is selected. `applyToPage: false` selects without re-applying/panning. */
  onSelectSearch?: (search: SelectedSavedSearch, options?: { applyToPage?: boolean }) => void;
  /** Called when a card is unselected. */
  onUnselectSearch?: () => void;
};

/**
 * Simple popover panel anchored to the top-bar bookmark button. See
 * `SavedSearchPanelContent` for the actual save/list/select/edit logic — also
 * reused by the mobile swipe-up sheet (`MobileSavedSearchSheet`).
 */
export function SavedSearchPanel({
  open,
  anchorEl,
  onClose,
  currentFilter,
  mapBounds,
  locatedByMap,
  onViewAllSavedSearches,
  selectedId,
  onSelectSearch,
  onUnselectSearch,
}: SavedSearchPanelProps) {
  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      PaperProps={{
        sx: {
          mt: 1,
          width: 420, // nice
          maxWidth: '100vw',
          borderRadius: '24px',
          boxShadow: '0 14px 40px rgba(15,23,41,0.16)',
          overflow: 'hidden',
        },
      }}
    >
      <SavedSearchPanelContent
        open={open}
        onClose={onClose}
        currentFilter={currentFilter}
        mapBounds={mapBounds}
        locatedByMap={locatedByMap}
        onViewAllSavedSearches={onViewAllSavedSearches}
        selectedId={selectedId}
        onSelectSearch={onSelectSearch}
        onUnselectSearch={onUnselectSearch}
      />
    </Popover>
  );
}
